import {
  Button,
  ButtonGroup,
  H4,
  H5,
  Input,
  Message,
  Stack,
  P,
  Spinner,
  Box
} from "@adfenix/alexandria";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ThinMessageWrapper, tryLoginAws } from "./common/helper";
import { useForm } from "react-hook-form";
import AuthPage from "components/AuthPage";
import styled from "styled-components";
import { useCognito } from "hooks/useCognito";
import { useCallback, useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import { useTranslation } from "react-i18next";
import {
  getCognitoConfig,
  getUserStatusPublic,
  UserStatusResponse
} from "api/services/authService";
import amplify from "lib/amplify/amplify";
import { useQuery } from "@tanstack/react-query";
import {
  getAmplifyConfigFromStorage,
  setAmplifyConfigToStorage,
  removeAmplifyConfigFromStorage,
  getFederatedLoginError,
  removeFederatedLoginError,
  setSsoReturnUrl
} from "api/ssoHelper";
import { LOGIN_EMAIL_QUERY_STRING_PARAM } from "utils/constants";
import { getSubjectIdForProgramInstance } from "api/services/programService";
import * as DOMPurify from "dompurify";
import { propertyPublicInformation } from "api/services/propertyService";
import { ErrorContent } from "api/http";
import { AuthService } from "@adfenix/types";

type UserStatus = "Unknown" | "OK" | "NoAccount";

type LoginData = {
  email: string;
  password: string;
};
const CenteredHeading = styled(H4)`
  text-align: center;
`;

const StyledButton = styled(Button)`
  margin-bottom: 10px !important;
`;

const LoginPage = () => {
  const [searchParams] = useSearchParams();
  let returnUrl = searchParams.get("returnUrl");
  if (returnUrl) returnUrl = decodeURIComponent(returnUrl);

  let queryEmail = searchParams.get("email");
  if (queryEmail) queryEmail = decodeURIComponent(queryEmail);

  const [propertyId, setPropertyId] = useState<string | null>(
    searchParams.get("propertyId")
  );
  const programInstanceId = searchParams.get("programInstanceId");
  const [error, setError] = useState<string | undefined>(undefined);
  const [internalLogin, setInternalLogin] = useState(false);
  const [externalLogin, setExternalLogin] = useState(false);
  const [cognitoConfigured, setCognitoConfigured] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [userStatus, setUserStatus] = useState<UserStatus>("Unknown");
  const showLogin = internalLogin && userStatus === "OK";
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { persistCognitoPayload } = useCognito();

  const { register, handleSubmit, watch, reset, setValue } = useForm<LoginData>(
    {
      mode: "onSubmit",
      defaultValues: {
        email: queryEmail ?? ""
      }
    }
  );

  const { email, password } = watch();

  const { data: property, isFetching: isLoadingProperty } = useQuery(
    ["propertyPublicInformation", { propertyId }],
    () => propertyPublicInformation({ propertyId: propertyId ?? "" }),
    {
      enabled: Boolean(propertyId)
    }
  );

  const { isFetching: isLoadingPrograminstance, data: subjectData } = useQuery(
    ["getSubjectIdForProgramInstance", { propertyId }],
    () => getSubjectIdForProgramInstance(programInstanceId ?? ""),
    {
      enabled: Boolean(programInstanceId)
    }
  );

  const {
    isFetching: isLoadingUserStatus,
    refetch: fetchUserStatus,
    data: userStatusData,
    error: userStatusError
  } = useQuery(
    ["getUserStatusPublic", { email }],
    () => getUserStatusPublic(email),
    {
      enabled: false,
      retry: 3
    }
  );

  const { data: cognitoData } = useQuery(
    ["getCognitoConfig", { email }],
    () => getCognitoConfig(email),
    {
      enabled: Boolean(isSubmitted) && !cognitoConfigured,
      retry: 3
    }
  );

  const forgetPassword = (email: string) =>
    navigate(
      `/password?${LOGIN_EMAIL_QUERY_STRING_PARAM}=${encodeURIComponent(email)}`
    );

  const onSubmit = async () => {
    removeFederatedLoginError();
    setIsSubmitted(true);
  };

  const clearLoginType = useCallback((status: UserStatus) => {
    setUserStatus(status);
    setInternalLogin(false);
    setExternalLogin(false);
    setCognitoConfigured(false);
    setError(undefined);
  }, []);

  const internalloginProcess = useCallback(
    async (values: LoginData) => {
      const user = await tryLoginAws({
        values,
        persistCognitoPayload,
        setError
      });

      if (user?.challengeName === "NEW_PASSWORD_REQUIRED") {
        navigate("password/temporary", {
          state: {
            ...values
          }
        });
      }

      if (returnUrl) {
        navigate(returnUrl);
      }

      setIsSubmitted(false);
    },
    [navigate, persistCognitoPayload, returnUrl]
  );

  const externalloginProcess = async () => {
    const cognitoConfig: AuthService.IdentityProviderPublicResponse =
      getAmplifyConfigFromStorage();

    Auth.federatedSignIn({
      customProvider: cognitoConfig.samlProviderName
    });
  };

  const errorFetchingUserStatus = useCallback(
    (error: ErrorContent) => {
      setInternalLogin(true);
      if (error.status === 404) {
        clearLoginType("NoAccount");
      }
    },
    [clearLoginType]
  );

  const parseUserStatus = useCallback(
    async (userStatus: UserStatusResponse) => {
      setInternalLogin(true);

      if (userStatus.userStatus === "FORCE_CHANGE_PASSWORD") {
        let setupAccountUrl =
          "/setup-account?email=" + encodeURIComponent(email);

        if (returnUrl)
          setupAccountUrl += "&returnUrl=" + encodeURIComponent(returnUrl);

        navigate(setupAccountUrl);
      } else {
        setUserStatus("OK");
      }
    },
    [email, navigate, returnUrl]
  );

  const configLoginProcess = useCallback(
    async (cognitoConfig: AuthService.IdentityProviderPublicResponse) => {
      if (!cognitoConfigured) {
        setAmplifyConfigToStorage(cognitoConfig);

        if (returnUrl && !cognitoConfig.defaultProvider) {
          setSsoReturnUrl(returnUrl);
        }

        amplify(cognitoConfig);
        setCognitoConfigured(true);
        if (cognitoConfig.defaultProvider) {
          setExternalLogin(false);
          fetchUserStatus();
        } else {
          setInternalLogin(false);
          setExternalLogin(true);
        }
      } else {
      }
    },
    [cognitoConfigured, fetchUserStatus, returnUrl]
  );

  const onError = (errors: unknown) => {
    console.log(errors);
  };

  const getFederatedSignInError = () => {
    const error = getFederatedLoginError();

    switch (error) {
      case "user federated sign in failed":
        return t("login.federated.error.title");
      default:
        return undefined;
    }
  };

  const getLoginError = (error?: string) => {
    switch (error) {
      case "Incorrect username or password.":
        return t("login.error.invalid.title");
      case "Password attempts exceeded":
        return t("login.cognito.resetPassword.card.codeError.attempError");
      default:
        return undefined;
    }
  };

  //catch network error mostly, all errors not related to code verification
  const getGeneralError = (error?: string) => {
    switch (error) {
      case "Incorrect username or password.":
      case "Password attempts exceeded":
        return undefined;
      default:
        return error;
    }
  };

  useEffect(() => {
    if (subjectData) {
      setPropertyId(subjectData);
    }
  }, [subjectData]);

  useEffect(() => {
    if (userStatusData) {
      parseUserStatus(userStatusData);
    }
  }, [parseUserStatus, userStatusData]);

  useEffect(() => {
    if (userStatusError) {
      errorFetchingUserStatus(userStatusError as unknown as ErrorContent);
    }
  }, [errorFetchingUserStatus, userStatusError]);

  useEffect(() => {
    if (cognitoData && !cognitoConfigured) {
      configLoginProcess(cognitoData);
    }
  }, [cognitoConfigured, cognitoData, configLoginProcess]);

  useEffect(() => {
    if (internalLogin && email && password && isSubmitted) {
      internalloginProcess({ email: email, password: password });
    }
  }, [email, internalLogin, internalloginProcess, isSubmitted, password]);

  useEffect(() => {
    if (property) {
      setValue("email", property.agentEmail);
    }
  }, [property, setValue]);

  useEffect(() => {
    if (queryEmail) {
      setIsSubmitted(true);
    }
  }, [queryEmail]);

  useEffect(() => {
    if (cognitoConfigured) {
      if (externalLogin) {
        externalloginProcess();
      } else setIsSubmitted(false);
    } else {
      removeAmplifyConfigFromStorage();
      removeFederatedLoginError();
    }
  }, [cognitoConfigured, setIsSubmitted, externalLogin]);

  return (
    <AuthPage>
      <form onSubmit={handleSubmit(onSubmit, onError)}>
        <Stack spacing="xl2">
          <Stack spacing="m">
            {getFederatedSignInError() && (
              <ThinMessageWrapper>
                <Message type="error">{getFederatedSignInError()}</Message>
              </ThinMessageWrapper>
            )}
            {getGeneralError(error) && (
              <ThinMessageWrapper>
                <Message type="error">{getGeneralError(error)}</Message>
              </ThinMessageWrapper>
            )}

            <CenteredHeading>{t("login.cognito.card.title")}</CenteredHeading>

            {(isLoadingPrograminstance || isLoadingProperty) && (
              <Box display="flex" justifyContent="center">
                <Spinner size={32} />
              </Box>
            )}

            {!isLoadingProperty && !isLoadingPrograminstance && (
              <Input
                label={t("login.cognito.card.email.label")}
                autoFocus
                type="email"
                data-loglabel="login-email-field"
                readOnly={internalLogin && userStatus === "OK"}
                {...register("email", { required: true })}
                placeholder={t("login.cognito.card.email.placeholder")}
                onChange={() => {
                  clearLoginType("Unknown");
                }}
              />
            )}

            {showLogin && (
              <Stack spacing="s">
                <Input
                  label={t("login.cognito.card.password.label")}
                  type="password"
                  data-loglabel="login-password-field"
                  required
                  placeholder={t("login.cognito.card.password.placeholder")}
                  autoFocus
                  {...register("password", { required: true })}
                />

                {getLoginError(error) && (
                  <ThinMessageWrapper>
                    <Message type="error">{getLoginError(error)}</Message>
                  </ThinMessageWrapper>
                )}
              </Stack>
            )}
          </Stack>

          {userStatus === "NoAccount" && (
            <ThinMessageWrapper>
              <H5 style={{ color: "#F50100" }}>
                {t("login_no_account_header")}
              </H5>
              <P
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(t("login_no_account_body"))
                }}
              ></P>
            </ThinMessageWrapper>
          )}

          <ButtonGroup alignItems="stretch" flexDirection="column">
            <StyledButton
              type="submit"
              variant="primary"
              isLoading={isSubmitted || isLoadingUserStatus}
              disabled={userStatus === "NoAccount"}
              logLabel={`login-${
                userStatus === "OK" ? "full" : "email"
              }-button`}
            >
              {t("login.cognito.card.loginButton")}
            </StyledButton>

            {internalLogin && userStatus === "OK" && (
              <StyledButton
                variant="secondary"
                logLabel="login-back-button"
                onClick={async () => {
                  clearLoginType("Unknown");
                  reset();
                }}
              >
                {t("order.footer.backButton")}
              </StyledButton>
            )}

            {internalLogin && userStatus === "OK" && (
              <Button
                onClick={() => forgetPassword(email)}
                variant="text"
                logLabel="login-forgot-password-button"
              >
                {t("login.cognito.card.doNotKnowPasswordButton")}
              </Button>
            )}
          </ButtonGroup>
        </Stack>
      </form>
    </AuthPage>
  );
};

export default LoginPage;
